import Chart from "react-apexcharts";
import { dmmSubdimensionsResults } from "../../../services/api-services";
import { useEffect, useState, useMemo, useCallback } from "react";
import { API_RESPONSE } from "../../../utills/app-constant";
import { toastEmitter } from "../../../utills/utilities";
import generateChartImage from "./generateChartImage";
import Remark from "./Remark";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const DmmSubdimensionsChart = ({ selectedCompanyId, graphName }) => {
  const [remark, setRemark] = useState("");
  const [remarkId, setRemarkId] = useState(null);
  const [isAttempt, setIsAttempt] = useState("");
  const [chartData, setChartData] = useState({ series: [], categories: [] });
  const [loading, setLoading] = useState(false);
  const [rowData, setRowdata] = useState([]);

  const options = useMemo(() => ({
    chart: {
      id: "DMM_Sub_Dimension",
      type: "bar",
      events: {
        mounted: () => generateChartImage("DMM_Sub_Dimension", "DMM_Sub_Dimension", remark),
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        dataLabels: {
          position: "top",
        },
      },
    },
    dataLabels: {
      enabled: true,
      offsetX: 20,
      style: {
        fontSize: "12px",
        colors: ["#304758"],
      },
    },
    colors: ["#557D39", "#F7BB1C", "#D42327"],
    xaxis: {
      categories: chartData.categories,
      labels: {
        style: {
          fontSize: "3px",
          fontWeight: "300px",
        },
        min: 1.0,
        max: 10.0,
        tickAmount: 10,
        formatter: (value) => value?.toFixed(2),
      },
    },
    // tooltip: { shared: true, intersect: false },
    legend: { position: "top" },
    stroke: { width: 1.5 },
  }), [chartData.categories, remark]);

  const getAllDmmSubdimensionsResults = useCallback(async () => {
    setLoading(true);
    try {
      const response = await dmmSubdimensionsResults(selectedCompanyId, graphName);
      if (response?.data?.responseCode === 200) {
        setRowdata(response?.data)
        const data = response?.data?.responseBody;
        const categories = [];
        const importanceScores = [];
        const currentScores = [];
        const futureScores = [];

        setRemark(response?.data?.remark || "");
        setRemarkId(response?.data?.remarkId || null);
        setIsAttempt(response?.data?.isAttempt ?? 0);

        data.forEach((dimension) => {
          dimension.subDimensions.forEach((subDimension) => {
            categories.push(`${dimension.dimensionName} (${subDimension.sortName})`);
            importanceScores.push(subDimension.importantAverageValue?.toFixed(2));
            currentScores.push(subDimension.currentAverageValue?.toFixed(2));
            futureScores.push(subDimension.futureAverageValue?.toFixed(2));
          });
        });

        setChartData({
          categories,
          series: [
            { name: "Current Score", data: currentScores },
            { name: "Future Score", data: futureScores },
            { name: "Importance Score", data: importanceScores },
          ],
        });
      } else {
        setChartData({ series: [], categories: [] });
        if (response?.data?.responseCode !== 404) {
          toastEmitter("error", response?.data?.message);
        }
      }
    } catch (err) {
      toastEmitter("error", API_RESPONSE.MESSAGE_503);
    } finally {
      setLoading(false);
    }
  }, [selectedCompanyId, graphName]);

  useEffect(() => {
    getAllDmmSubdimensionsResults();
  }, [getAllDmmSubdimensionsResults]);

  const exportSummaryToExcel = (data, fileName = "DMM Sub-Dimension Results") => {
    if (!data || !data.responseBody) return;

    const title = ["DMM Sub-Dimension Results"];
    const headers = [
      "Dimension Name",
      "Subdimension Name",
      "Important Average",
      "Current Average",
      "Future Average"
    ];

    const rows = [];
    data.responseBody.forEach(dimension => {
      dimension.subDimensions?.forEach(sub => {
        rows.push([
          dimension.dimensionName,
          sub.subDimensionName,
          sub.importantAverageValue,
          sub.currentAverageValue,
          sub.futureAverageValue
        ]);
      });
    });

    // Create worksheet with title, empty row, headers, and data
    const worksheet = XLSX.utils.aoa_to_sheet([
      title,
      [], // empty row for spacing
      headers,
      ...rows
    ]);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Subdimensions");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, fileName);
  };


  return (
    <>
      <div className="text-end">
        <button className="px-3 py-2 text-white bg-primary rounded border-0" onClick={() => exportSummaryToExcel(rowData)}>Download Excel</button>
      </div>
      <div className="subChart">
        {loading ? (
          <div className="loader-container">
            <div className="loader"></div>
          </div>
        ) : (
          <div className="chart7" data-heading="DMM Sub-Dimension results" data-remark={remark}>
            <Chart options={options} series={chartData.series} type="bar" />
          </div>
        )}
        <Remark
          graphName="DMMSubDimensionresults"
          selectedCompanyId={selectedCompanyId}
          initialRemark={remark}
          remarkId={remarkId}
          setRemarkprops={setRemark}
          isAttempt={isAttempt}
        />
      </div>
    </>

  );
};

export default DmmSubdimensionsChart;
